import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { aaiApi } from '../../services/aaiApi';
import type { ConnectorRow, ConnectorScope, ConnectorStatus } from '../../types/aai';
import { Action, Chip } from '../aa';

const SETTLED = new Set(['ACTIVE', 'FAILED', 'EXPIRED', 'INACTIVE']);
const STILL_FINISHING = new Set(['INITIATED', 'INITIALIZING']);

const settle = async (slug: string, scope: ConnectorScope): Promise<ConnectorStatus | null> => {
  let last: ConnectorStatus | null = null;
  for (let i = 0; i < 8; i++) {
    try {
      const { data } = await aaiApi.getConnectorStatus(slug, scope);
      last = data;
      if (data.connected || SETTLED.has((data.status || '').toUpperCase())) return data;
    } catch { /* the sign-in window may still be finishing */ }
    await new Promise((resolve) => setTimeout(resolve, 1300));
  }
  return last;
};

export const confirmConnected = async (slug: string, scope: ConnectorScope = 'personal'): Promise<boolean> => {
  const st = await settle(slug, scope);
  return Boolean(st?.connected);
};

const failureReason = (label: string, status: string): string => {
  if (status === 'FAILED') return `${label} did not connect. The sign-in was refused or a permission was left unticked. Try again and allow every permission.`;
  if (status === 'EXPIRED') return `${label} sign-in expired before it finished. Try again.`;
  if (status === 'DISABLED_BY_ORGANIZATION') return `${label} is switched off for your company. Ask the Owner to turn it on.`;
  return `${label} did not connect. The sign-in window closed before it finished. Try again.`;
};

/* After the sign-in window closes (or the tab comes back from it), wait for
   Composio to settle and say what happened, including why it failed. A grant
   still finishing gets one more round instead of being reported as a failure. */
export const finishConnect = async (slug: string, scope: ConnectorScope, fallbackLabel: string): Promise<boolean> => {
  let st = await settle(slug, scope);
  let label = st?.label || fallbackLabel;
  if (!st?.connected && STILL_FINISHING.has((st?.status || '').toUpperCase())) {
    toast.info(`${label} is still finishing. It will show as connected in a moment.`);
    st = await settle(slug, scope);
    label = st?.label || label;
  }
  if (st?.connected) {
    if (scope === 'personal') toast.success(`${label} connected. It stays yours alone.`);
    else toast.success(`${label} connected for the company.`);
    return true;
  }
  toast.error(failureReason(label, (st?.status || '').toUpperCase()));
  return false;
};

/* A blocked popup takes the whole tab through the sign-in. The callback sends
   it back to /settings?tab=connectors&connecting=<slug>; the redirect parks the
   slug here so the Connectors screen can finish the check once it mounts. */
const RETURN_KEY = 'aa.connecting';

export const rememberConnectReturn = (slug: string, scope: string): void => {
  try { window.sessionStorage.setItem(RETURN_KEY, JSON.stringify({ slug, scope })); } catch { /* storage unavailable */ }
};

const takeConnectReturn = (): { slug: string; scope: ConnectorScope } | null => {
  try {
    const raw = window.sessionStorage.getItem(RETURN_KEY);
    window.sessionStorage.removeItem(RETURN_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.slug) return null;
    let scope: ConnectorScope = 'personal';
    if (parsed.scope === 'organization') scope = 'organization';
    return { slug: String(parsed.slug), scope };
  } catch {
    return null;
  }
};

export const usePersonalConnect = (onDone: () => void) => {
  const [busy, setBusy] = useState('');
  const pollRef = useRef<number>(0);

  useEffect(() => () => window.clearInterval(pollRef.current), []);

  useEffect(() => {
    const pending = takeConnectReturn();
    if (!pending) return;
    setBusy(pending.slug);
    finishConnect(pending.slug, pending.scope, 'The app').finally(() => {
      setBusy('');
      onDone();
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connect = async (row: ConnectorRow) => {
    setBusy(row.slug);
    try {
      const { data } = await aaiApi.initiateConnector(row.slug, 'personal');
      if (data.alreadyConnected) {
        if (data.connectedNow) toast.success(`${row.label} connected. It stays yours alone.`);
        else toast.success(`${row.label} was already authorized for your account.`);
        setBusy('');
        onDone();
        return;
      }
      if (!data.redirectUrl) {
        toast.error(`Could not start the ${row.label} connection. Try again in a moment.`);
        setBusy('');
        return;
      }
      const popup = window.open(data.redirectUrl, `connect-${row.slug}`, 'width=600,height=720,menubar=no,toolbar=no');
      if (!popup || popup.closed) {
        window.location.href = data.redirectUrl;
        return;
      }
      pollRef.current = window.setInterval(async () => {
        if (!popup.closed) return;
        window.clearInterval(pollRef.current);
        await finishConnect(row.slug, 'personal', row.label);
        setBusy('');
        onDone();
      }, 900);
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      toast.error(detail || `Could not connect ${row.label}.`);
      setBusy('');
    }
  };

  return { busy, connect };
};

export const PersonalConnectControl: React.FC<{ row: ConnectorRow; busy: boolean; onConnect: () => void }> = ({ row, busy, onConnect }) => {
  if (row.personal_status === 'connected') return <Chip tone="ok">Connected</Chip>;
  const companyAccount = row.organization_status === 'connected';
  let label = 'Connect';
  if (companyAccount) label = 'Use my own';
  if (busy) label = 'Connecting…';
  return (
    <span className="flex items-center gap-2">
      {companyAccount && <Chip tone="ok">Company account</Chip>}
      <Action size="sm" variant="quiet" onClick={onConnect} disabled={busy} reason="Connecting">{label}</Action>
    </span>
  );
};
